import React from 'react';
import { CommissionBasis } from '@shopify-multiseller/graphql/partner/types';
import { Page } from '../../components';
import { AdminUI } from '../../ui/AdminUI';
import { SectionCard } from '../../components/SectionCard';
import { useAuth, useSettings, useShopCurrency } from '../../hooks';
import { formatCents } from '../../helpers/formatters';
import { SettingsRail } from './SettingsRail';
import { settingsDestinations } from './destinations';

const Row: React.FC<{ label: string; value: React.ReactNode; help?: string }> = ({
  label,
  value,
  help,
}) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2.5 last:border-b-0">
    <span>
      <span className="block text-sm text-foreground">{label}</span>
      {help && <span className="block text-xs text-muted-foreground">{help}</span>}
    </span>
    <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{value}</span>
  </div>
);

/**
 * What the shop charges, in one read-only page.
 *
 * The account menu has always offered "Selling Fees" and it has always led to
 * a 404 — the numbers were only ever visible one product at a time, in the
 * fees panel of the editor. They are the shop's terms, so they are stated
 * plainly and cannot be edited here.
 */
export const SellingFees: React.FC = () => {
  const auth = useAuth();
  const settings = useSettings();
  const currencyCode = useShopCurrency();
  const destinations = settingsDestinations(auth?.user?.seller?.settings?.storefront?.enabled);

  const commission = settings?.productPercentage?.productPercentage ?? 0;
  const productFee = settings?.productFee?.productFee ?? 0;
  const orderFee = settings?.orderFee?.orderFee ?? 0;
  const shippingCommission = settings?.shippingPercentage?.shippingPercentage ?? 0;
  const deductions = settings?.deductions?.deductions ?? [];
  const onProfit = settings?.commissionBasis?.basis === CommissionBasis.Profit;
  const payoutPeriod = settings?.payoutPeriod?.payoutPeriod;

  const none = <span className="text-muted-foreground">None</span>;

  return (
    <AdminUI>
      <Page title="Selling fees">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[12rem_1fr]">
          <SettingsRail destinations={destinations} className="lg:sticky lg:top-20 lg:self-start" />
          <div className="min-w-0 max-w-2xl space-y-5">
            <SectionCard title="Commission &amp; fees" testId="selling-fees-terms">
              <Row
                label="Commission"
                help={
                  onProfit
                    ? 'Charged on profit — the sale price less your cost of goods.'
                    : 'Charged on the product total.'
                }
                value={commission > 0 ? `${commission}%` : none}
              />
              <Row
                label="Product fee"
                help="A fixed amount per product sold."
                value={productFee > 0 ? formatCents({ amount: productFee, currencyCode }) : none}
              />
              <Row
                label="Order fee"
                help="A fixed amount per order, however many products it contains."
                value={orderFee > 0 ? formatCents({ amount: orderFee, currencyCode }) : none}
              />
              <Row
                label="Shipping commission"
                help="A share of the shipping the customer paid."
                value={shippingCommission > 0 ? `${shippingCommission}%` : none}
              />
            </SectionCard>

            {deductions.length > 0 && (
              <SectionCard title="Other deductions" testId="selling-fees-deductions">
                {deductions.map((deduction, index) => (
                  <Row
                    key={`${deduction.name}-${index}`}
                    label={deduction.name}
                    value={
                      deduction.type === 'PERCENTAGE'
                        ? `${deduction.amount}% of order total`
                        : formatCents({ amount: deduction.amount, currencyCode })
                    }
                  />
                ))}
              </SectionCard>
            )}

            <SectionCard title="Payouts" testId="selling-fees-payouts">
              <Row
                label="Payout schedule"
                help="How long after fulfilment your money settles."
                value={payoutPeriod ? `${payoutPeriod} days` : none}
              />
            </SectionCard>

            <p className="text-xs text-muted-foreground">
              These are set by the shop. Every product and order shows what it will cost you before
              you commit to it.
            </p>
          </div>
        </div>
      </Page>
    </AdminUI>
  );
};
